import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams, Link } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import Sidebar from '../Components/Sidebar/Sidebar';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import { ensureProtocol } from '../../config/backend';
import { fetchTiposChamadoTodos } from '../../api/cadastroChamados';
import { fetchAnalistas } from '../../api/usuarios';
import { getAnalistaDisplayNome } from '../../FuncoesGerais/tipoAnalista';
import { useStatusChamado } from '../../hooks/useStatusChamado';
import { isStatusChamadoFechado } from '../../api/statusChamado';
import { fetchRepositorioSprint } from '../../api/repositorios';
import {
    fetchSprints,
    fetchSprint,
    fetchChamadosSprint,
    fetchBurndownSprint,
    fetchSatNumsEmSprint,
    createSprint,
    updateSprint,
    ativarSprint,
    concluirSprint,
    addChamadosSprint,
    removeChamadoSprint,
    moverChamadoSprint,
    iniciarSessaoSprint,
    pausarSessaoSprint
} from '../../api/sprints';
import { ModalConfirm } from '../Components/ModalSistema';
import './Sprint.css';

const STATUS_COLUNAS = [
    { key: 'backlog', label: 'Backlog' },
    { key: 'fazendo', label: 'Fazendo' },
    { key: 'revisao', label: 'Revisão' },
    { key: 'concluido', label: 'Concluído' }
];

function isItemConcluidoNaSprint(item) {
    return String(item?.status || '').toLowerCase() === 'concluido';
}

function buildClientBurndown(detalhe, chamados) {
    if (!detalhe || !detalhe.data_inicio || !detalhe.data_fim) return [];
    const parse = (v) => {
        const s = String(v).slice(0, 10);
        const [y, m, d] = s.split('-').map(Number);
        if (!y || !m || !d) return null;
        const dt = new Date(y, m - 1, d);
        dt.setHours(0, 0, 0, 0);
        return dt;
    };
    const inicio = parse(detalhe.data_inicio);
    const fim = parse(detalhe.data_fim);
    if (!inicio || !fim) return [];
    const msDia = 24 * 60 * 60 * 1000;
    const totalDias = Math.max(1, Math.round((fim - inicio) / msDia) + 1);
    const lista = Array.isArray(chamados) ? chamados : [];
    const totalHoras = lista.reduce((acc, c) => acc + (Number(c.estimativa_horas) || 0), 0);
    const remaining = lista.reduce((acc, c) => {
        if ((c.status || '').toLowerCase() === 'concluido') return acc;
        const est = Number(c.estimativa_horas) || 0;
        const trab = Number(c.tempo_trabalhado_horas) || 0;
        return acc + Math.max(0, est - trab);
    }, 0);
    const gasto = lista.reduce((acc, c) => acc + (Number(c.tempo_trabalhado_horas) || 0), 0);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    let diaHoje = Math.round((hoje - inicio) / msDia);
    if (diaHoje < 0) diaHoje = 0;
    if (diaHoje > totalDias - 1) diaHoje = totalDias - 1;
    return Array.from({ length: totalDias }, (_, dia) => {
        const dt = new Date(inicio.getTime() + dia * msDia);
        const ideal = totalDias <= 1 ? 0 : Math.round(totalHoras * (1 - dia / (totalDias - 1)) * 10) / 10;
        const isHoje = dia === diaHoje;
        return {
            dia: dia + 1,
            label: dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
            ideal,
            real: isHoje ? Math.round(remaining * 10) / 10 : null,
            trabalhado: isHoje ? Math.round(gasto * 10) / 10 : null
        };
    });
}

function resolverBurndown(pontos, detalhe, chamados) {
    if (Array.isArray(pontos) && pontos.length > 0) return pontos;
    return buildClientBurndown(detalhe, chamados);
}

function Sprint() {
    const history = useHistory();
    const location = useLocation();
    const { sprintId: sprintIdParam } = useParams();
    const { statusList } = useStatusChamado();
    const [sprints, setSprints] = useState([]);
    const [sprintSelecionada, setSprintSelecionada] = useState(null);
    const [detalheSprint, setDetalheSprint] = useState(null);
    const [chamados, setChamados] = useState([]);
    const [burnDownData, setBurnDownData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingChamados, setLoadingChamados] = useState(false);
    const [mensagem, setMensagem] = useState('');
    const [showModalNova, setShowModalNova] = useState(false);
    const [showModalEditar, setShowModalEditar] = useState(false);
    const [showModalAdicionar, setShowModalAdicionar] = useState(false);
    const [novaNome, setNovaNome] = useState('');
    const [novaDataInicio, setNovaDataInicio] = useState('');
    const [novaDataFim, setNovaDataFim] = useState('');
    const [novaResponsavelId, setNovaResponsavelId] = useState('');
    const [analistas, setAnalistas] = useState([]);
    const [filtroListaAdicionar, setFiltroListaAdicionar] = useState('');
    const [estimativasPorChamado, setEstimativasPorChamado] = useState({});
    const [prioridadePorChamado, setPrioridadePorChamado] = useState({});
    const [dependenciasPorChamado, setDependenciasPorChamado] = useState({});
    const [chamadosAbertos, setChamadosAbertos] = useState([]);
    const [, setTiposChamado] = useState([]);
    const [selecionadosAdicionar, setSelecionadosAdicionar] = useState([]);
    const [loadingAdicionar, setLoadingAdicionar] = useState(false);
    const [arrastando, setArrastando] = useState(null);
    const [showJustificativaConcluir, setShowJustificativaConcluir] = useState(false);
    const [itemParaConcluir, setItemParaConcluir] = useState(null);
    const [justificativaAtraso, setJustificativaAtraso] = useState('');
    const [dragOverColunaKey, setDragOverColunaKey] = useState(null);
    const [panelLateralAberto, setPanelLateralAberto] = useState(false);
    const [arrastandoExterno, setArrastandoExterno] = useState(null);
    const [miniFormChamado, setMiniFormChamado] = useState(null);
    const [miniFormPrioridade, setMiniFormPrioridade] = useState('media');
    const [miniFormEstimativa, setMiniFormEstimativa] = useState('');
    const [miniFormDependeDe, setMiniFormDependeDe] = useState([]);
    const [loadingPanel, setLoadingPanel] = useState(false);
    const [filtroPanel, setFiltroPanel] = useState('');
    const [editNome, setEditNome] = useState('');
    const [editDataInicio, setEditDataInicio] = useState('');
    const [editDataFim, setEditDataFim] = useState('');
    const [editResponsavelId, setEditResponsavelId] = useState('');
    const [editWipFazendo, setEditWipFazendo] = useState('');
    const [editWipRevisao, setEditWipRevisao] = useState('');
    const [showModalConfirmarConcluirSprint, setShowModalConfirmarConcluirSprint] = useState(false);
    const [showModalConcluirSprint, setShowModalConcluirSprint] = useState(false);
    const [justificativaConclusaoSprint, setJustificativaConclusaoSprint] = useState('');
    const [confirmRemoverChamado, setConfirmRemoverChamado] = useState({ show: false, satNum: null });

    const verificar = useCallback(async () => {
        const resultado = await verificandoApp('ATENDENTE');
        if (!resultado) history.push('/');
    }, [history]);

    const abrirRepositorioSprint = useCallback(async () => {
        if (!sprintSelecionada) return;
        verificandoToken();
        try {
            const repo = await fetchRepositorioSprint(sprintSelecionada);
            if (repo && repo.id) history.push(`/repositorios/${repo.id}`);
            else setMensagem('Não foi possível abrir o repositório da sprint.');
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Não foi possível abrir o repositório da sprint.');
        }
    }, [sprintSelecionada, history]);

    const loadSprints = useCallback(async () => {
        verificandoToken();
        try {
            const list = await fetchSprints();
            const arr = Array.isArray(list) ? list : [];
            setSprints(arr);
        } catch (e) {
            setSprints([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        verificar();
        loadSprints();
    }, [verificar, loadSprints]);

    useEffect(() => {
        const idFromRoute = sprintIdParam ? parseInt(sprintIdParam, 10) : null;
        const params = new URLSearchParams(location.search || '');
        const idFromQuery = params.get('sprint') ? parseInt(params.get('sprint'), 10) : null;
        const id = idFromRoute || idFromQuery;
        if (id && !Number.isNaN(id)) {
            setSprintSelecionada(id);
        }
    }, [sprintIdParam, location.search]);

    const reloadChamadosEBurndown = useCallback(async (sprintId) => {
        const id = sprintId ?? sprintSelecionada;
        if (!id) return;
        const [list, pontos] = await Promise.all([
            fetchChamadosSprint(id),
            fetchBurndownSprint(id)
        ]);
        const lista = Array.isArray(list) ? list : [];
        setChamados(lista);
        setBurnDownData(resolverBurndown(pontos, detalheSprint, lista));
    }, [sprintSelecionada, detalheSprint]);

    useEffect(() => {
        if (!sprintSelecionada) {
            setDetalheSprint(null);
            setChamados([]);
            setBurnDownData([]);
            return;
        }
        let cancelled = false;
        setLoadingChamados(true);
        Promise.all([
            fetchSprint(sprintSelecionada),
            fetchChamadosSprint(sprintSelecionada),
            fetchBurndownSprint(sprintSelecionada)
        ])
            .then(([det, list, pontos]) => {
                if (!cancelled) {
                    const lista = Array.isArray(list) ? list : [];
                    setDetalheSprint(det);
                    setChamados(lista);
                    setBurnDownData(resolverBurndown(pontos, det, lista));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDetalheSprint(null);
                    setChamados([]);
                    setBurnDownData([]);
                }
            })
            .finally(() => { if (!cancelled) setLoadingChamados(false); });
        return () => { cancelled = true; };
    }, [sprintSelecionada]);

    const handleCriarSprint = async () => {
        if (!novaNome.trim() || !novaDataInicio || !novaDataFim) {
            setMensagem('Preencha nome, data início e data fim.');
            return;
        }
        setMensagem('');
        verificandoToken();
        try {
            const payload = {
                nome: novaNome.trim(),
                data_inicio: novaDataInicio,
                data_fim: novaDataFim,
                responsavel_id: (novaResponsavelId !== '' && novaResponsavelId != null) ? Number(novaResponsavelId) : null
            };
            await createSprint(payload);
            setShowModalNova(false);
            setNovaNome('');
            setNovaDataInicio('');
            setNovaDataFim('');
            setNovaResponsavelId('');
            await loadSprints();
            setMensagem('');
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao criar sprint.');
        }
    };

    const abrirModalEditar = async () => {
        if (!detalheSprint || !sprintSelecionada) return;
        setEditNome(detalheSprint.nome || '');
        setEditDataInicio(detalheSprint.data_inicio ? String(detalheSprint.data_inicio).slice(0, 10) : '');
        setEditDataFim(detalheSprint.data_fim ? String(detalheSprint.data_fim).slice(0, 10) : '');
        setEditResponsavelId(detalheSprint.responsavel_id != null ? String(detalheSprint.responsavel_id) : '');
        setEditWipFazendo(detalheSprint.wip_fazendo != null ? String(detalheSprint.wip_fazendo) : '');
        setEditWipRevisao(detalheSprint.wip_revisao != null ? String(detalheSprint.wip_revisao) : '');
        setMensagem('');
        try {
            const list = await fetchAnalistas();
            setAnalistas(Array.isArray(list) ? list : []);
        } catch (_) {
            setAnalistas([]);
        }
        setShowModalEditar(true);
    };

    const handleSalvarEdicao = async () => {
        if (!sprintSelecionada || !editNome.trim() || !editDataInicio || !editDataFim) {
            setMensagem('Preencha nome, data início e data fim.');
            return;
        }
        setMensagem('');
        verificandoToken();
        try {
            const wipFazendo = editWipFazendo !== '' && !Number.isNaN(Number(editWipFazendo)) && Number(editWipFazendo) >= 0 ? Number(editWipFazendo) : null;
            const wipRevisao = editWipRevisao !== '' && !Number.isNaN(Number(editWipRevisao)) && Number(editWipRevisao) >= 0 ? Number(editWipRevisao) : null;
            const payload = {
                nome: editNome.trim(),
                data_inicio: editDataInicio,
                data_fim: editDataFim,
                responsavel_id: (editResponsavelId !== '' && editResponsavelId != null) ? Number(editResponsavelId) : null,
                wip_fazendo: wipFazendo,
                wip_revisao: wipRevisao
            };
            await updateSprint(sprintSelecionada, payload);
            setShowModalEditar(false);
            const d = await fetchSprint(sprintSelecionada);
            setDetalheSprint(d);
            setMensagem('');
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao salvar.');
        }
    };

    const formatarData = (d) => {
        if (!d) return '-';
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return '-';
        return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const baseUrlAnalista = () => localStorage.getItem('ipBackend2') ? ensureProtocol(localStorage.getItem('ipBackend2')) : '';

    const handleAtivar = async (id) => {
        verificandoToken();
        try {
            await ativarSprint(id);
            await loadSprints();
            if (sprintSelecionada === id) {
                const d = await fetchSprint(id);
                setDetalheSprint(d);
            }
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao ativar.');
        }
    };

    const handleConcluirSprint = async (comJustificativa) => {
        if (!sprintSelecionada) return;
        verificandoToken();
        const justificativa = comJustificativa ? justificativaConclusaoSprint : undefined;
        try {
            await concluirSprint(sprintSelecionada, justificativa);
            setShowModalConcluirSprint(false);
            setJustificativaConclusaoSprint('');
            setMensagem('');
            await loadSprints();
            const d = await fetchSprint(sprintSelecionada);
            setDetalheSprint(d);
        } catch (e) {
            const data = e?.response?.data;
            if (e?.response?.status === 400 && data?.exige_justificativa) {
                setShowModalConcluirSprint(true);
                setMensagem('');
            } else {
                setMensagem(data?.error || 'Erro ao concluir a sprint.');
            }
        }
    };

    const handleMover = async (sprintChamadoId, novoStatus, novaOrdem, justificativaAtraso) => {
        if (!sprintSelecionada) return;
        verificandoToken();
        try {
            const payload = { status: novoStatus, ordem: novaOrdem };
            if (justificativaAtraso != null && String(justificativaAtraso).trim() !== '') payload.justificativa_atraso = String(justificativaAtraso).trim();
            await moverChamadoSprint(sprintSelecionada, sprintChamadoId, payload);
            await reloadChamadosEBurndown();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao mover.');
        }
    };

    const statusChamadoItem = (item) => item?.SATSTASAI ?? item?.sat_stasai ?? null;

    const rotaChamadoSprint = (item) => {
        const satNum = item?.sat_num;
        if (satNum == null) return '/home';
        if (isStatusChamadoFechado(statusList, statusChamadoItem(item))) {
            return `/historicoSatiFechada/${satNum}`;
        }
        return `/historicoSati/${satNum}`;
    };

    const itemPermiteSessao = (item) => (
        !isItemConcluidoNaSprint(item) && !isStatusChamadoFechado(statusList, statusChamadoItem(item))
    );

    const abrirChamadoSprint = (item, e) => {
        if (e?.target?.closest('button, a')) return;
        history.push(rotaChamadoSprint(item));
    };

    const handlePlay = async (item) => {
        if (!itemPermiteSessao(item)) return;
        const sprintChamadoId = item.sprint_chamado_id;
        const satNum = item.sat_num;
        verificandoToken();
        const token = localStorage.getItem('token');
        const colabo = localStorage.getItem('codUsu');
        try {
            await iniciarSessaoSprint(sprintChamadoId);
            if (satNum && colabo && baseUrlAnalista()) {
                await axios.put(
                    baseUrlAnalista() + '/satis/pegar',
                    { colabo, satNum, contexto: 'desenvolvimento' },
                    { headers: { Authorization: token } }
                );
            }
            await reloadChamadosEBurndown();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao iniciar sessão.');
        }
    };

    const handlePause = async (item) => {
        if (!itemPermiteSessao(item)) return;
        verificandoToken();
        try {
            await pausarSessaoSprint(item.sprint_chamado_id);
            await reloadChamadosEBurndown();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao pausar.');
        }
    };

    const handleRemover = (satNum) => {
        if (!sprintSelecionada) return;
        setConfirmRemoverChamado({ show: true, satNum });
    };

    const executarRemoverChamado = async () => {
        const satNum = confirmRemoverChamado.satNum;
        setConfirmRemoverChamado({ show: false, satNum: null });
        if (!sprintSelecionada || !satNum) return;
        verificandoToken();
        try {
            await removeChamadoSprint(sprintSelecionada, satNum);
            await reloadChamadosEBurndown();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao remover.');
        }
    };

    const loadChamadosElegiveis = useCallback(async () => {
        if (!sprintSelecionada) return;
        setLoadingPanel(true);
        verificandoToken();
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('codUsu');
        try {
            const [resSatis, tipos, satNumsEmSprint] = await Promise.all([
                axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/satis', {
                    headers: { Authorization: token },
                    params: { user, colab: '1,' + user, descr: '', solici: '' }
                }),
                fetchTiposChamadoTodos(),
                fetchSatNumsEmSprint()
            ]);
            const tiposSprint = (Array.isArray(tipos) ? tipos : []).filter((t) => t.usa_sprint === 1).map((t) => t.id);
            const raw = Array.isArray(resSatis?.data) ? resSatis.data : [];
            const elegiveis = raw.filter((r) => tiposSprint.includes(Number(r.SATTIP ?? r.tipo)));
            const naSprint = new Set((satNumsEmSprint || []).map((n) => Number(n)));
            const disponiveis = elegiveis.filter((r) => {
                const id = Number(r.id ?? r.SATNUM ?? 0);
                return id && !naSprint.has(id);
            });
            setChamadosAbertos(disponiveis);
            setTiposChamado(tipos || []);
        } catch (e) {
            setChamadosAbertos([]);
        } finally {
            setLoadingPanel(false);
        }
    }, [sprintSelecionada]);

    const togglePanelLateral = async () => {
        if (!sprintSelecionada) return;
        if (panelLateralAberto) {
            setPanelLateralAberto(false);
            return;
        }
        await loadChamadosElegiveis();
        setPanelLateralAberto(true);
    };

    const abrirModalAdicionar = async () => {
        if (!sprintSelecionada) return;
        setSelecionadosAdicionar([]);
        setEstimativasPorChamado({});
        setPrioridadePorChamado({});
        setDependenciasPorChamado({});
        setFiltroListaAdicionar('');
        await loadChamadosElegiveis();
        setShowModalAdicionar(true);
    };

    const confirmarAdicionar = async () => {
        if (!sprintSelecionada || selecionadosAdicionar.length === 0) return;
        setLoadingAdicionar(true);
        setMensagem('');
        verificandoToken();
        try {
            const itens = selecionadosAdicionar.map((id) => {
                const raw = estimativasPorChamado[id];
                const est = raw !== undefined && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null;
                const pri = prioridadePorChamado[id];
                const prioridade = ['alta', 'media', 'baixa'].includes(pri) ? pri : 'media';
                const dependeDe = Array.isArray(dependenciasPorChamado[id]) ? dependenciasPorChamado[id] : [];
                return { sat_num: id, estimativa_horas: est, prioridade, depende_de: dependeDe };
            });
            await addChamadosSprint(sprintSelecionada, [], null, itens);
            setShowModalAdicionar(false);
            await reloadChamadosEBurndown();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao adicionar.');
        } finally {
            setLoadingAdicionar(false);
        }
    };

    const setEstimativaChamado = (id, value) => {
        setEstimativasPorChamado((prev) => ({ ...prev, [id]: value }));
    };

    const setPrioridadeChamado = (id, value) => {
        setPrioridadePorChamado((prev) => ({ ...prev, [id]: value }));
    };

    const setDependenciasChamado = (id, sprintChamadoIds) => {
        setDependenciasPorChamado((prev) => ({ ...prev, [id]: Array.isArray(sprintChamadoIds) ? sprintChamadoIds : [] }));
    };

    const prioridadeItem = (item) => {
        const p = item && item.prioridade ? String(item.prioridade).toLowerCase() : 'media';
        return p === 'média' || p === 'media' ? 'media' : (['alta', 'baixa'].includes(p) ? p : 'media');
    };
    const prioridadeLabel = (p) => ({ alta: 'Alta', media: 'Média', baixa: 'Baixa' }[p] || 'Média');

    const formatarDataLista = (d) => {
        if (!d) return '—';
        const dt = new Date(d);
        return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const toggleSelecaoAdicionar = (id) => {
        setSelecionadosAdicionar((prev) => {
            const jaIncluido = prev.includes(id);
            if (jaIncluido) return prev.filter((x) => x !== id);
            return [...prev, id];
        });
        setPrioridadePorChamado((prev) => ({ ...prev, [id]: prev[id] ?? 'media' }));
        setDependenciasPorChamado((prev) => ({ ...prev, [id]: prev[id] ?? [] }));
    };

    const chamadosFiltradosAdicionar = () => {
        const txt = (filtroListaAdicionar || '').trim().toLowerCase();
        if (!txt) return chamadosAbertos;
        return chamadosAbertos.filter((c) => {
            const num = String(c.id ?? c.SATNUM ?? '').toLowerCase();
            const desc = String(c.SATDESSOL ?? c.descricao ?? '').toLowerCase();
            return num.includes(txt) || desc.includes(txt);
        });
    };

    const chamadosFiltradosPanel = () => {
        const txt = (filtroPanel || '').trim().toLowerCase();
        if (!txt) return chamadosAbertos;
        return chamadosAbertos.filter((c) => {
            const num = String(c.id ?? c.SATNUM ?? '').toLowerCase();
            const desc = String(c.SATDESSOL ?? c.descricao ?? '').toLowerCase();
            return num.includes(txt) || desc.includes(txt);
        });
    };

    const confirmarMiniForm = async () => {
        if (!sprintSelecionada || !miniFormChamado) return;
        const est = miniFormEstimativa !== '' && !Number.isNaN(Number(miniFormEstimativa)) ? Number(miniFormEstimativa) : null;
        setMensagem('');
        verificandoToken();
        try {
            await addChamadosSprint(sprintSelecionada, [], null, [{
                sat_num: miniFormChamado.id,
                estimativa_horas: est,
                prioridade: ['alta', 'media', 'baixa'].includes(miniFormPrioridade) ? miniFormPrioridade : 'media',
                depende_de: Array.isArray(miniFormDependeDe) ? miniFormDependeDe : []
            }]);
            await reloadChamadosEBurndown();
            await loadChamadosElegiveis();
            setMiniFormChamado(null);
            setMiniFormPrioridade('media');
            setMiniFormEstimativa('');
            setMiniFormDependeDe([]);
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao adicionar.');
        }
    };

    const chamadosPorStatus = (status) => chamados.filter((c) => (c.status || 'backlog') === status);

    const burnDownAtraso = useMemo(() => {
        if (burnDownData.length === 0) return false;
        const hojeIdx = Math.round((new Date() - new Date(detalheSprint.data_inicio)) / (24 * 60 * 60 * 1000));
        if (hojeIdx < 0 || hojeIdx >= burnDownData.length) return false;
        const ponto = burnDownData[hojeIdx];
        return ponto && ponto.real != null && ponto.ideal != null && ponto.real > ponto.ideal;
    }, [burnDownData, detalheSprint]);

    const horasEstimadasVsTrabalhadas = useMemo(() => {
        const estimada = chamados.reduce((acc, c) => acc + (Number(c.estimativa_horas) || 0), 0);
        const gastoExecucao = chamados.reduce((acc, c) => {
            if ((c.status || '').toLowerCase() === 'concluido') return acc;
            return acc + (Number(c.tempo_trabalhado_horas) || 0);
        }, 0);
        const gastoConcluido = chamados.reduce((acc, c) => {
            if ((c.status || '').toLowerCase() !== 'concluido') return acc;
            return acc + (Number(c.tempo_trabalhado_horas) || 0);
        }, 0);
        return [
            { tipo: 'Planejado', valor: Math.round(estimada * 10) / 10, fill: 'var(--bs-primary)' },
            { tipo: 'Em execução', valor: Math.round(gastoExecucao * 10) / 10, fill: 'var(--bs-warning)' },
            { tipo: 'Concluído', valor: Math.round(gastoConcluido * 10) / 10, fill: 'var(--bs-success)' }
        ];
    }, [chamados]);

    const totalGasto = useMemo(
        () => chamados.reduce((acc, c) => acc + (Number(c.tempo_trabalhado_horas) || 0), 0),
        [chamados]
    );

    const previsaoSprint = useMemo(() => {
        if (!detalheSprint || !detalheSprint.data_inicio || !detalheSprint.data_fim) {
            return { tipo: 'indefinida', dias: null, mensagem: 'Defina o período da sprint para prever o prazo.' };
        }

        const dataInicio = new Date(detalheSprint.data_inicio);
        const dataFim = new Date(detalheSprint.data_fim);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim.setHours(0, 0, 0, 0);

        const msDia = 24 * 60 * 60 * 1000;
        const diasPassados = Math.max(1, Math.round((hoje - dataInicio) / msDia) + 1);
        const diasRestantes = Math.max(0, Math.round((dataFim - hoje) / msDia) + 1);

        const horasConcluidas = chamados.reduce((acc, c) => {
            if ((c.status || '').toLowerCase() !== 'concluido') return acc;
            return acc + (Number(c.estimativa_horas) || 0);
        }, 0);

        if (horasConcluidas <= 0) {
            return { tipo: 'indefinida', dias: null, mensagem: 'Ainda é cedo para prever. Conclua pelo menos uma tarefa.' };
        }

        const velocidade = horasConcluidas / diasPassados;
        if (velocidade <= 0) {
            return { tipo: 'indefinida', dias: null, mensagem: 'Ainda é cedo para prever. Conclua pelo menos uma tarefa.' };
        }

        const horasRestantes = chamados.reduce((acc, c) => {
            if ((c.status || '').toLowerCase() === 'concluido') return acc;
            const est = Number(c.estimativa_horas) || 0;
            const trab = Number(c.tempo_trabalhado_horas) || 0;
            return acc + Math.max(0, est - trab);
        }, 0);

        if (horasRestantes <= 0) {
            return { tipo: 'adiantada', dias: 0, mensagem: 'Sprint já concluída com as horas planejadas.' };
        }

        const diasNecessarios = horasRestantes / velocidade;

        const diff = diasNecessarios - diasRestantes;
        if (diff > 0.5) {
            const atraso = Math.ceil(diff);
            return {
                tipo: 'atraso',
                dias: atraso,
                mensagem: `Sprint tende a atrasar cerca de ${atraso} dia${atraso > 1 ? 's' : ''} no ritmo atual.`
            };
        }

        if (diff < -0.5) {
            const folga = Math.floor(-diff);
            return {
                tipo: 'adiantada',
                dias: folga,
                mensagem: `Sprint tende a terminar até ${folga} dia${folga > 1 ? 's' : ''} antes no ritmo atual.`
            };
        }

        return {
            tipo: 'no_limite',
            dias: 0,
            mensagem: 'Sprint no limite do prazo pelo ritmo atual.'
        };
    }, [detalheSprint, chamados]);

    const totalHorasEstimadas = useMemo(
        () => chamados.reduce((acc, c) => acc + (Number(c.estimativa_horas) || 0), 0),
        [chamados]
    );

    const tempoRestantePct = Math.min(100, Math.max(0, 100 - (Number(detalheSprint?.percentual_tempo) ?? 0)));

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="container-fluid titulo cadastro-unificado sprint-page">
                    <div className="page-header">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <Link to="/sprint" className="btn btn-link btn-sm p-0 text-muted text-decoration-none">
                                <i className="fas fa-arrow-left me-1" />Voltar para sprints
                            </Link>
                        </div>
                        <h1 className="mb-2">Sprint de Chamados</h1>
                        <div className="toolbar flex-wrap align-items-center gap-2">
                            <select
                                value={sprintSelecionada || ''}
                                onChange={(e) => {
                                    const id = e.target.value ? Number(e.target.value) : null;
                                    if (id) history.push(`/sprint/${id}`);
                                }}
                                className="form-control form-control-sm"
                                style={{ maxWidth: 260 }}
                            >
                                <option value="">Selecione uma sprint</option>
                                {sprints.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.nome} {s.ativo ? '(ativa)' : ''}
                                    </option>
                                ))}
                            </select>
                            <button type="button" className="btn btn-primary btn-sm" onClick={async () => {
                                setMensagem('');
                                setShowModalNova(true);
                                try {
                                    const list = await fetchAnalistas();
                                    setAnalistas(Array.isArray(list) ? list : []);
                                    const codUsu = localStorage.getItem('codUsu');
                                    setNovaResponsavelId(codUsu && codUsu !== '0' ? String(codUsu) : '');
                                } catch (_) {
                                    setAnalistas([]);
                                }
                            }}>
                                Nova sprint
                            </button>
                            {sprintSelecionada && (
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={togglePanelLateral} title="Abrir painel e arrastar chamados para o board">
                                    {panelLateralAberto ? 'Fechar painel' : 'Adicionar à sprint'}
                                </button>
                            )}
                            {sprintSelecionada && (
                                <button type="button" className="btn btn-outline-secondary btn-sm" title="Repositório de arquivos da sprint" onClick={abrirRepositorioSprint}>
                                    <i className="fas fa-code-branch me-1" />Repositório
                                </button>
                            )}
                        </div>
                    </div>

                    {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}

                    {loading && <p className="text-muted">Carregando sprints...</p>}

                    {!loading && sprintSelecionada && detalheSprint && (
                        <>

                            <div className="card sprint-info-card mb-3">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-stretch gap-3 mb-2">
                                        <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0">
                                            <h5 className="card-title mb-0">{detalheSprint.nome || 'Sprint'}</h5>
                                            <div className="sprint-meta-line text-muted small">
                                                <span><i className="fas fa-user-tie me-1" />{detalheSprint.responsavel_nome || 'Não definido'}</span>
                                                <span className="sprint-meta-sep">|</span>
                                                <span><i className="fas fa-calendar-alt me-1" />{formatarData(detalheSprint.data_inicio)} a {formatarData(detalheSprint.data_fim)}</span>
                                                <span className="sprint-meta-sep">|</span>
                                                <span>
                                                    {detalheSprint.concluida_em ? (
                                                        <span className="badge bg-secondary">Fechada</span>
                                                    ) : detalheSprint.ativo === 1 ? (
                                                        <span className="badge bg-success">Sprint ativa</span>
                                                    ) : (detalheSprint.total_chamados ?? 0) === 0 ? (
                                                        <span className="text-muted" title="Adicione chamados antes de ativar."><i className="fas fa-info-circle me-1" />Adicione chamados para ativar</span>
                                                    ) : (
                                                        <button type="button" className="btn btn-sm btn-success" onClick={() => handleAtivar(sprintSelecionada)}>Ativar esta sprint</button>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="d-flex flex-column gap-1 flex-shrink-0">
                                            {!detalheSprint.concluida_em && (
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirModalEditar} title="Editar nome, responsável e período"><i className="fas fa-edit me-1" />Editar sprint</button>
                                            )}
                                            {(() => {
                                                const codUsu = localStorage.getItem('codUsu');
                                                const responsavelId = detalheSprint.responsavel_id ?? detalheSprint.criado_por;
                                                const ehResponsavel = codUsu != null && String(codUsu).trim() !== '' && Number(codUsu) === Number(responsavelId);
                                                const podeConcluir = ehResponsavel && detalheSprint.ativo === 1 && !detalheSprint.concluida_em;
                                                return podeConcluir ? (
                                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setShowModalConfirmarConcluirSprint(true)} title="Todos os itens devem estar na coluna Concluído"><i className="fas fa-check-double me-1" />Concluir sprint</button>
                                                ) : null;
                                            })()}
                                        </div>
                                    </div>
                                    {detalheSprint.concluida_em && (
                                        <div className="sprint-cards-conclusao row g-2 mb-0 mt-2">
                                            <div className="col-auto">
                                                <div className="sprint-metrica-card sprint-metrica-card-concluido h-100">
                                                    <div className="sprint-metrica-card-label">Concluído</div>
                                                    <div className="sprint-metrica-card-value">{detalheSprint.concluida_em ? String(detalheSprint.concluida_em).substring(0, 10) : '—'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="sprint-cards-metricas row g-2 mb-0">
                                        <div className="col-6 col-md-3">
                                            <div className="sprint-metrica-card h-100">
                                                <div className="sprint-metrica-card-label">Tempo restante</div>
                                                <div className="sprint-metrica-card-value">{tempoRestantePct}%</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="sprint-metrica-card h-100">
                                                <div className="sprint-metrica-card-label">Progresso</div>
                                                <div className="sprint-metrica-card-value">{detalheSprint.percentual_conclusao ?? 0}%</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="sprint-metrica-card h-100">
                                                <div className="sprint-metrica-card-label">Itens</div>
                                                <div className="sprint-metrica-card-value">{detalheSprint.concluidos ?? 0} / {detalheSprint.total_chamados ?? 0}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="sprint-metrica-card h-100">
                                                <div className="sprint-metrica-card-label">Horas da sprint</div>
                                                <div className="sprint-metrica-card-value">{totalHorasEstimadas}h</div>
                                                <div className="sprint-metrica-card-value-small">est. · {Math.round(totalGasto * 10) / 10}h trab.</div>
                                            </div>
                                        </div>
                                    </div>
                                    {previsaoSprint.tipo !== 'indefinida' && (
                                        <div className="mt-2">
                                            {previsaoSprint.tipo === 'atraso' && (
                                                <span className="badge bg-warning text-dark" title="Previsão automática com base nas horas concluídas e nas datas da sprint">
                                                    <i className="fas fa-exclamation-triangle me-1" aria-hidden="true" />
                                                    {previsaoSprint.mensagem}
                                                </span>
                                            )}
                                            {previsaoSprint.tipo === 'adiantada' && (
                                                <span className="badge bg-success" title="Previsão automática com base nas horas concluídas e nas datas da sprint">
                                                    <i className="fas fa-check-circle me-1" aria-hidden="true" />
                                                    {previsaoSprint.mensagem}
                                                </span>
                                            )}
                                            {previsaoSprint.tipo === 'no_limite' && (
                                                <span className="badge bg-secondary" title="Previsão automática com base nas horas concluídas e nas datas da sprint">
                                                    <i className="fas fa-balance-scale me-1" aria-hidden="true" />
                                                    {previsaoSprint.mensagem}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {detalheSprint.meta && (
                                        <p className="card-text text-muted small mb-0 mt-2"><strong>Meta:</strong> {detalheSprint.meta}</p>
                                    )}
                                </div>
                            </div>

                            {loadingChamados ? (
                                <p className="text-muted">Carregando itens...</p>
                            ) : (
                                <>
                                    {chamados.length === 0 && (
                                        <div className="alert alert-info py-2 mb-3 d-flex align-items-center gap-2">
                                            <i className="fas fa-plus-circle" />
                                            <span>Nenhum chamado nesta sprint. Use o botão <strong>Adicionar à sprint</strong> para abrir o painel e arrastar chamados para o board.</span>
                                        </div>
                                    )}
                                    <div className="sprint-legend-prioridade mb-2">
                                        <span className="sprint-legend-label">Prioridade:</span>
                                        <span className="sprint-legend-item sprint-legend-alta"><span className="sprint-legend-dot" /> Alta</span>
                                        <span className="sprint-legend-item sprint-legend-media"><span className="sprint-legend-dot" /> Média</span>
                                        <span className="sprint-legend-item sprint-legend-baixa"><span className="sprint-legend-dot" /> Baixa</span>
                                        <span className="sprint-legend-dica text-muted small">— Definida ao adicionar o chamado à sprint</span>
                                    </div>
                                <hr className="sprint-section-sep" />
                                <div className="sprint-board">
                                    {STATUS_COLUNAS.map((col) => {
                                        const count = chamadosPorStatus(col.key).length;
                                        const wipMax = col.key === 'fazendo' ? (detalheSprint && detalheSprint.wip_fazendo != null ? Number(detalheSprint.wip_fazendo) : null) : col.key === 'revisao' ? (detalheSprint && detalheSprint.wip_revisao != null ? Number(detalheSprint.wip_revisao) : null) : null;
                                        const wipExcedido = wipMax != null && count > wipMax;
                                        return (
                                        <div key={col.key} className={`sprint-coluna sprint-coluna--${col.key} ${wipExcedido ? 'sprint-coluna-wip-excedido' : ''}`}>
                                            <div className="sprint-coluna-titulo" title={wipExcedido ? `Limite de WIP excedido (máx. ${wipMax})` : undefined}>
                                                <span className="sprint-coluna-nome">{col.label}</span>
                                                <span className="sprint-coluna-count">{wipMax != null ? `${count}/${wipMax}` : count}</span>
                                                {wipExcedido && <span className="sprint-coluna-wip-alerta" title={`Limite de WIP excedido (máx. ${wipMax})`}><i className="fas fa-exclamation-triangle" /></span>}
                                            </div>
                                            <div
                                                className={`sprint-coluna-cards ${(arrastando && dragOverColunaKey === col.key) || (arrastandoExterno && dragOverColunaKey === col.key) ? 'sprint-coluna-drag-over' : ''}`}
                                                onDragEnter={() => (arrastando || arrastandoExterno) && setDragOverColunaKey(col.key)}
                                                onDragLeave={() => setDragOverColunaKey(null)}
                                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = arrastandoExterno ? 'copy' : 'move'; }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setDragOverColunaKey(null);
                                                    const externalId = e.dataTransfer.getData('application/x-sprint-chamado-externo');
                                                    if (externalId) {
                                                        setArrastandoExterno(null);
                                                        const id = parseInt(externalId, 10);
                                                        const chamado = chamadosAbertos.find((c) => c.id === id);
                                                        if (chamado) {
                                                            setMiniFormChamado(chamado);
                                                            setMiniFormPrioridade('media');
                                                            setMiniFormEstimativa('');
                                                            setMiniFormDependeDe([]);
                                                        }
                                                        return;
                                                    }
                                                    if (!arrastando || arrastando.status === col.key) { setArrastando(null); return; }
                                                    const estourou = col.key === 'concluido' && arrastando.estimativa_horas != null && arrastando.tempo_trabalhado_horas != null && arrastando.tempo_trabalhado_horas > arrastando.estimativa_horas;
                                                    if (estourou) {
                                                        setItemParaConcluir({ sprintChamadoId: arrastando.sprint_chamado_id, satNum: arrastando.sat_num, novaOrdem: undefined });
                                                        setJustificativaAtraso('');
                                                        setShowJustificativaConcluir(true);
                                                    } else {
                                                        handleMover(arrastando.sprint_chamado_id, col.key);
                                                    }
                                                    setArrastando(null);
                                                }}
                                            >
                                                {count === 0 && <p className="sprint-coluna-vazia">Nenhum item</p>}
                                                {chamadosPorStatus(col.key).map((item) => {
                                                    const concluidoNaSprint = isItemConcluidoNaSprint(item);
                                                    const fechadoNoSistema = isStatusChamadoFechado(statusList, statusChamadoItem(item));
                                                    const permiteSessao = itemPermiteSessao(item);
                                                    const rotaChamado = rotaChamadoSprint(item);
                                                    return (
                                                    <div
                                                        key={item.sprint_chamado_id}
                                                        className={`sprint-card sprint-card-prioridade-${prioridadeItem(item)}${concluidoNaSprint ? ' sprint-card--concluido' : ''}`}
                                                        draggable
                                                        onDragStart={(ev) => { setArrastando(item); ev.dataTransfer.effectAllowed = 'move'; ev.dataTransfer.setData('text/plain', item.sprint_chamado_id); }}
                                                        onDragEnd={() => { setArrastando(null); setDragOverColunaKey(null); }}
                                                        onClick={concluidoNaSprint ? (e) => abrirChamadoSprint(item, e) : undefined}
                                                        onKeyDown={concluidoNaSprint ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirChamadoSprint(item, e); } } : undefined}
                                                        role={concluidoNaSprint ? 'button' : undefined}
                                                        tabIndex={concluidoNaSprint ? 0 : undefined}
                                                        title={concluidoNaSprint ? 'Clique para ver o chamado' : undefined}
                                                    >
                                                        <div className="sprint-card-header" title={`${item.SATDESSOL || 'Descrição do chamado'} — Prioridade: ${prioridadeLabel(prioridadeItem(item))}`}>
                                                            <span className="sprint-card-num">#{item.sat_num}</span>
                                                            {!detalheSprint.concluida_em && detalheSprint.ativo !== 1 && (
                                                                <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={(e) => { e.stopPropagation(); handleRemover(item.sat_num); }} title="Remover da sprint"><i className="fas fa-times" /></button>
                                                            )}
                                                        </div>
                                                        <div className="sprint-card-desc truncate" title={item.SATDESSOL || 'Descrição do chamado'}>{item.SATDESSOL || '-'}</div>
                                                        {(item.depende_de && item.depende_de.length > 0) && (
                                                            <div className="sprint-card-depende-de small text-muted" title="Conclua estes itens antes de mover para Fazendo/Revisão">
                                                                <i className="fas fa-link me-1" aria-hidden="true" />
                                                                Depende de: {item.depende_de.map((d) => `#${d.sat_num}`).join(', ')}
                                                            </div>
                                                        )}
                                                        <div className="sprint-card-responsavel">
                                                            <i className="fas fa-user me-1" aria-hidden="true" />
                                                            <span className="sprint-card-responsavel-nome">{(item.responsavel_nome ?? item.RESPONSAVEL_NOME ?? '').trim() || '—'}</span>
                                                        </div>
                                                        <div className="sprint-card-metricas">
                                                            {(() => {
                                                                const est = item.estimativa_horas != null ? Number(item.estimativa_horas) : 0;
                                                                const trab = item.tempo_trabalhado_horas != null ? Number(item.tempo_trabalhado_horas) : 0;
                                                                const pct = est > 0 ? Math.min(100, (trab / est) * 100) : 0;
                                                                return (
                                                                    <>
                                                                        {est > 0 && (
                                                                            <div className="sprint-card-mini-bar" title={`${trab}h de ${est}h`}>
                                                                                <div className="sprint-card-mini-bar-track">
                                                                                    <div className="sprint-card-mini-bar-fill" style={{ width: `${pct}%` }} />
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        <div className="sprint-card-metricas-text">
                                                                            {trab}h / {est > 0 ? est + 'h' : '—'}
                                                                            {item.indicador_atraso && <span className="text-warning ms-1" title="Possível atraso"><i className="fas fa-exclamation-triangle" /></span>}
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                        <div className="sprint-card-actions">
                                                            {permiteSessao && (
                                                                <>
                                                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={(e) => { e.stopPropagation(); handlePlay(item); }} title="Play"><i className="fas fa-play" /></button>
                                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); handlePause(item); }} title="Pause"><i className="fas fa-pause" /></button>
                                                                </>
                                                            )}
                                                            <Link to={rotaChamado} onClick={(e) => e.stopPropagation()} className="btn btn-sm btn-outline-primary" title={concluidoNaSprint || fechadoNoSistema ? 'Ver chamado' : 'Abrir chamado'}>
                                                                <i className={`fas ${concluidoNaSprint || fechadoNoSistema ? 'fa-eye' : 'fa-external-link-alt'}`} />
                                                            </Link>
                                                        </div>
                                                    </div>
                                                );})}
                                            </div>
                                        </div>
                                        );
                                    })}
                                    {detalheSprint.concluida_em && (() => {
                                        const dataFimCard = detalheSprint.data_fim ? new Date(detalheSprint.data_fim) : null;
                                        const dataConclusaoCard = detalheSprint.concluida_em ? new Date(detalheSprint.concluida_em) : null;
                                        if (dataFimCard) dataFimCard.setHours(0, 0, 0, 0);
                                        if (dataConclusaoCard) dataConclusaoCard.setHours(0, 0, 0, 0);
                                        const atrasada = dataFimCard != null && dataConclusaoCard != null && dataConclusaoCard > dataFimCard;
                                        const labelCard = atrasada ? 'Justificativa' : 'Observação';
                                        return (
                                        <div className="sprint-coluna sprint-coluna-observacao">
                                            <div className="sprint-coluna-titulo">{labelCard}</div>
                                            <div className="sprint-coluna-cards sprint-coluna-cards-observacao">
                                                <div className="sprint-metrica-card-observacao-texto-block">
                                                    {(detalheSprint.justificativa_conclusao != null && String(detalheSprint.justificativa_conclusao).trim() !== '') ? detalheSprint.justificativa_conclusao : '—'}
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })()}
                                </div>

                                <hr className="sprint-section-sep" />
                                <div className="sprint-rodape-analise row g-3">
                                    <div className="col-lg-6">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <h6 className="card-title mb-2">
                                                    Burn Down da sprint
                                                    {burnDownAtraso && (
                                                        <span className="badge bg-warning text-dark ms-2" title="Horas restantes acima da linha ideal">Atraso</span>
                                                    )}
                                                </h6>
                                                {burnDownData.length === 0 ? (
                                                    <p className="text-muted small mb-0">
                                                        {!detalheSprint?.data_inicio || !detalheSprint?.data_fim
                                                            ? 'Defina o período da sprint (Editar sprint) para ver o gráfico.'
                                                            : 'Não foi possível carregar o burn down. Reinicie o backend Analista (rota /sprints/:id/burndown) e recarregue a página.'}
                                                    </p>
                                                ) : (
                                                    <>
                                                        {totalHorasEstimadas <= 0 && (
                                                            <p className="text-muted small mb-2">Informe a <strong>estimativa em horas</strong> nos itens da sprint para a linha Ideal.</p>
                                                        )}
                                                        <div className="sprint-burndown-chart">
                                                            <ResponsiveContainer width="100%" height={220}>
                                                                <LineChart data={burnDownData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                                    <CartesianGrid strokeDasharray="3 3" />
                                                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                                                    <YAxis tick={{ fontSize: 11 }} unit=" h" />
                                                                    <Tooltip
                                                                        formatter={(value, name) => {
                                                                            const label = name === 'ideal' ? 'Planejado restante' : name === 'real' ? 'Real restante' : 'Trabalhado (acum.)';
                                                                            return [value != null ? `${value} h` : '—', label];
                                                                        }}
                                                                        labelFormatter={(label) => `Dia: ${label}`}
                                                                    />
                                                                    <Legend formatter={(value) => value === 'ideal' ? 'Planejado (restante)' : value === 'real' ? 'Real (restante)' : 'Trabalhado (acumulado)'} />
                                                                    <Line type="monotone" dataKey="ideal" stroke="var(--bs-primary)" strokeWidth={2} dot={false} name="ideal" />
                                                                    <Line type="monotone" dataKey="real" stroke="var(--bs-success)" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} name="real" connectNulls={false} />
                                                                    <Line type="monotone" dataKey="trabalhado" stroke="var(--bs-warning)" strokeWidth={2} dot={{ r: 3 }} name="trabalhado" connectNulls={false} />
                                                                </LineChart>
                                                            </ResponsiveContainer>
                                                        </div>
                                                        <p className="text-muted small mb-0 mt-1"><strong>Planejado restante</strong> (azul) × <strong>real restante</strong> (verde) × <strong>trabalhado acumulado</strong> (laranja). Atualiza ao vivo com Play/Pause e conclusões.</p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <h6 className="card-title mb-2">Horas: planejado × gasto (execução e concluído)</h6>
                                                {chamados.length === 0 ? (
                                                    <p className="text-muted small mb-0">Adicione itens à sprint para ver o gráfico.</p>
                                                ) : (totalHorasEstimadas === 0 && totalGasto === 0) ? (
                                                    <p className="text-muted small mb-0">Informe estimativas e registre tempo trabalhado nos itens.</p>
                                                ) : (
                                                    <div className="sprint-horas-chart">
                                                        <ResponsiveContainer width="100%" height={220}>
                                                            <BarChart data={horasEstimadasVsTrabalhadas} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                                <CartesianGrid strokeDasharray="3 3" />
                                                                <XAxis dataKey="tipo" tick={{ fontSize: 12 }} />
                                                                <YAxis tick={{ fontSize: 11 }} unit=" h" />
                                                                <Tooltip formatter={(value) => [`${value} h`, 'Horas']} />
                                                                <Bar dataKey="valor" name="Horas" radius={[4, 4, 0, 0]}>
                                                                    {horasEstimadasVsTrabalhadas.map((entry, index) => (
                                                                        <Cell key={index} fill={entry.fill} />
                                                                    ))}
                                                                </Bar>
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                        <p className="text-muted small mb-0 mt-1">Planejado (azul) × gasto em execução (laranja) × gasto em concluídos (verde).</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                </>
                            )}
                        </>
                    )}

                    {showJustificativaConcluir && itemParaConcluir && (
                        <div className="modal-overlay" onClick={() => setShowJustificativaConcluir(false)}>
                            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                                <h5 className="mb-2">Justificativa para concluir</h5>
                                <p className="text-muted small mb-3">O tempo trabalhado neste item estourou a estimativa. Informe uma justificativa para mover para Concluído.</p>
                                <div className="form-group mb-3">
                                    <label className="small">Justificativa (obrigatória)</label>
                                    <textarea value={justificativaAtraso} onChange={(e) => setJustificativaAtraso(e.target.value)} className="form-control form-control-sm" rows={3} placeholder="Ex.: Escopo ampliado; dependência externa; correção de bug encontrado..." />
                                </div>
                                <div className="d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowJustificativaConcluir(false); setItemParaConcluir(null); }}>Cancelar</button>
                                    <button type="button" className="btn btn-primary btn-sm" onClick={async () => {
                                        if (!justificativaAtraso.trim()) { setMensagem('Preencha a justificativa.'); return; }
                                        await handleMover(itemParaConcluir.sprintChamadoId, 'concluido', itemParaConcluir.novaOrdem, justificativaAtraso);
                                        setShowJustificativaConcluir(false);
                                        setItemParaConcluir(null);
                                        setJustificativaAtraso('');
                                        setMensagem('');
                                    }}>Concluir com justificativa</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showModalConfirmarConcluirSprint && (
                        <div className="modal-overlay" onClick={() => setShowModalConfirmarConcluirSprint(false)}>
                            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                                <h5 className="mb-2">Concluir sprint</h5>
                                <p className="text-muted small mb-3">Deseja realmente concluir a sprint? Em seguida você poderá informar uma observação ou justificativa (obrigatória se fora do prazo).</p>
                                <div className="d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModalConfirmarConcluirSprint(false)}>Cancelar</button>
                                    <button type="button" className="btn btn-success btn-sm" onClick={() => {
                                        setShowModalConfirmarConcluirSprint(false);
                                        setShowModalConcluirSprint(true);
                                        setJustificativaConclusaoSprint('');
                                        setMensagem('');
                                    }}>Sim, concluir</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showModalConcluirSprint && (() => {
                        const dataFim = detalheSprint?.data_fim ? new Date(detalheSprint.data_fim) : null;
                        const hoje = new Date();
                        hoje.setHours(0, 0, 0, 0);
                        if (dataFim) dataFim.setHours(0, 0, 0, 0);
                        const foraDoPrazo = dataFim != null && hoje > dataFim;
                        const labelCampo = foraDoPrazo ? 'Justificativa' : 'Observação';
                        const obrigatorio = foraDoPrazo;
                        return (
                            <div className="modal-overlay" onClick={() => setShowModalConcluirSprint(false)}>
                                <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                                    <h5 className="mb-2">{foraDoPrazo ? 'Justificativa para concluir a sprint' : 'Observação (opcional)'}</h5>
                                    <p className="text-muted small mb-3">
                                        {foraDoPrazo
                                            ? 'A sprint foi entregue após o prazo (data fim). Informe uma justificativa para concluir.'
                                            : 'Você pode registrar uma observação sobre a conclusão da sprint (opcional).'}
                                    </p>
                                    <div className="form-group mb-3">
                                        <label className="small">{labelCampo}{obrigatorio ? ' (obrigatória)' : ' (opcional)'}</label>
                                        <textarea value={justificativaConclusaoSprint} onChange={(e) => setJustificativaConclusaoSprint(e.target.value)} className="form-control form-control-sm" rows={3} placeholder={foraDoPrazo ? 'Ex.: Itens críticos finalizados; dependências externas...' : 'Ex.: Observações sobre a entrega...'} />
                                    </div>
                                    <div className="d-flex justify-content-end gap-2">
                                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowModalConcluirSprint(false); setJustificativaConclusaoSprint(''); }}>Cancelar</button>
                                        <button type="button" className="btn btn-primary btn-sm" onClick={async () => {
                                            if (obrigatorio && !justificativaConclusaoSprint.trim()) { setMensagem('Preencha a justificativa.'); return; }
                                            await handleConcluirSprint(true);
                                        }}>Concluir sprint</button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    <ModalConfirm
                        open={confirmRemoverChamado.show}
                        onClose={() => setConfirmRemoverChamado({ show: false, satNum: null })}
                        title="Remover chamado da sprint"
                        message="Remover este chamado da sprint?"
                        confirmLabel="Sim, remover"
                        cancelLabel="Cancelar"
                        onConfirm={executarRemoverChamado}
                        variant="danger"
                    />

                    {!loading && sprintSelecionada && !detalheSprint && !loadingChamados && <p className="text-muted">Sprint não encontrada.</p>}
                    {!loading && !sprintSelecionada && sprints.length === 0 && <p className="text-muted">Nenhuma sprint. Crie uma nova sprint.</p>}
                    {!loading && sprints.length > 0 && !sprintSelecionada && <p className="text-muted">Selecione uma sprint no menu acima para ver o board.</p>}
                </div>
            </main>

            {panelLateralAberto && sprintSelecionada && (
                <div className="sprint-panel-lateral">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Arrastar para sprint</h6>
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setPanelLateralAberto(false)} title="Fechar"><i className="fas fa-times" /></button>
                    </div>
                    <p className="small text-muted mb-2">Arraste um chamado para o board e defina prioridade e dependências ao soltar.</p>
                    <div className="form-group mb-2">
                        <input type="text" className="form-control form-control-sm" placeholder="Filtrar..." value={filtroPanel} onChange={(e) => setFiltroPanel(e.target.value)} />
                    </div>
                    <button type="button" className="btn btn-outline-secondary btn-sm w-100 mb-3" onClick={abrirModalAdicionar}>Adicionar em lote (tabela)</button>
                    {loadingPanel ? (
                        <p className="small text-muted">Carregando...</p>
                    ) : chamadosFiltradosPanel().length === 0 ? (
                        <p className="small text-muted">Nenhum chamado elegível ou todos já estão na sprint.</p>
                    ) : (
                        <div className="sprint-panel-lista">
                            {chamadosFiltradosPanel().map((c) => (
<div
                                                    key={c.id}
                                                    className="sprint-panel-draggable"
                                                    title={c.SATDESSOL || c.descricao || `Chamado #${c.id}`}
                                                    draggable
                                                    onDragStart={(ev) => {
                                                        setArrastandoExterno(c);
                                                        ev.dataTransfer.effectAllowed = 'copy';
                                                        ev.dataTransfer.setData('application/x-sprint-chamado-externo', String(c.id));
                                                    }}
                                                    onDragEnd={() => setArrastandoExterno(null)}
                                                >
                                                    <span className="sprint-panel-draggable-num">#{c.id}</span>
                                                    <span className="sprint-panel-draggable-desc">{c.SATDESSOL || c.descricao || '—'}</span>
                                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {miniFormChamado && (
                <div className="modal-overlay" onClick={() => setMiniFormChamado(null)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <h6 className="mb-2">Adicionar #{miniFormChamado.id} à sprint</h6>
                        <p className="small text-muted mb-2 truncate" style={{ maxWidth: '100%' }} title={miniFormChamado.SATDESSOL || miniFormChamado.descricao}>{miniFormChamado.SATDESSOL || miniFormChamado.descricao || '—'}</p>
                        <div className="form-group mb-2">
                            <label className="small">Prioridade na sprint</label>
                            <select className="form-select form-select-sm" value={miniFormPrioridade} onChange={(e) => setMiniFormPrioridade(e.target.value)}>
                                <option value="alta">Alta (vermelho)</option>
                                <option value="media">Média (amarelo)</option>
                                <option value="baixa">Baixa (verde)</option>
                            </select>
                        </div>
                        <div className="form-group mb-2">
                            <label className="small">Estimativa (h)</label>
                            <input type="number" min="0" step="0.5" className="form-control form-control-sm" placeholder="Opcional" value={miniFormEstimativa} onChange={(e) => setMiniFormEstimativa(e.target.value)} />
                        </div>
                        <div className="form-group mb-3">
                            <label className="small">Depende de (itens já na sprint)</label>
                            <select
                                multiple
                                className="form-select form-select-sm"
                                style={{ minHeight: 72 }}
                                value={miniFormDependeDe.map(String)}
                                onChange={(e) => setMiniFormDependeDe(Array.from(e.target.selectedOptions, (o) => Number(o.value)))}
                                title="Ctrl+clique para múltiplos"
                            >
                                {chamados.map((sc) => (
                                    <option key={sc.sprint_chamado_id} value={sc.sprint_chamado_id}>#{sc.sat_num}</option>
                                ))}
                                {chamados.length === 0 && <option disabled>Nenhum na sprint</option>}
                            </select>
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMiniFormChamado(null)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={confirmarMiniForm}>Adicionar</button>
                        </div>
                    </div>
                </div>
            )}

            {showModalEditar && (
                <div className="modal-overlay" onClick={() => setShowModalEditar(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3">Editar sprint</h5>
                        {detalheSprint && detalheSprint.ativo === 1 && (
                            <div className="alert alert-info py-2 mb-3 small">Sprint ativa: apenas o <strong>responsável</strong> pode ser alterado. Nome e datas ficam bloqueados.</div>
                        )}
                        <div className="form-group mb-2">
                            <label>Nome</label>
                            <input value={editNome} onChange={(e) => setEditNome(e.target.value)} className="form-control form-control-sm" placeholder="Ex.: Sprint 1" disabled={detalheSprint && detalheSprint.ativo === 1} />
                        </div>
                        <div className="form-group mb-2">
                            <label>Responsável (gerente / desenvolvedor)</label>
                            <select value={editResponsavelId} onChange={(e) => setEditResponsavelId(e.target.value)} className="form-control form-control-sm">
                                <option value="">Selecione...</option>
                                {analistas.map((a) => (
                                    <option key={a.USUSEQ ?? a.ususeq} value={a.USUSEQ ?? a.ususeq}>{getAnalistaDisplayNome(a.USRNOMCPL ?? a.usrnomcpl ?? a.USUSEQ ?? a.ususeq, a)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group mb-2">
                            <label>Data início</label>
                            <input type="date" value={editDataInicio} onChange={(e) => setEditDataInicio(e.target.value)} className="form-control form-control-sm" disabled={detalheSprint && detalheSprint.ativo === 1} />
                        </div>
                        <div className="form-group mb-2">
                            <label>Data fim</label>
                            <input type="date" value={editDataFim} onChange={(e) => setEditDataFim(e.target.value)} className="form-control form-control-sm" disabled={detalheSprint && detalheSprint.ativo === 1} />
                        </div>
                        <div className="form-group mb-2">
                            <label className="small">Limite WIP — Fazendo (máx.)</label>
                            <input type="number" min="0" className="form-control form-control-sm" placeholder="Vazio = sem limite" value={editWipFazendo} onChange={(e) => setEditWipFazendo(e.target.value)} title="Máximo de itens na coluna Fazendo; ao exceder, o sistema bloqueia novos movimentos" />
                        </div>
                        <div className="form-group mb-2">
                            <label className="small">Limite WIP — Revisão (máx.)</label>
                            <input type="number" min="0" className="form-control form-control-sm" placeholder="Vazio = sem limite" value={editWipRevisao} onChange={(e) => setEditWipRevisao(e.target.value)} title="Máximo de itens na coluna Revisão; ao exceder, o sistema bloqueia novos movimentos" />
                        </div>
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModalEditar(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={handleSalvarEdicao}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {showModalNova && (
                <div className="modal-overlay" onClick={() => setShowModalNova(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3">Nova sprint</h5>
                        <div className="form-group mb-2">
                            <label>Nome</label>
                            <input value={novaNome} onChange={(e) => setNovaNome(e.target.value)} className="form-control form-control-sm" placeholder="Ex.: Sprint 1" />
                        </div>
                        <div className="form-group mb-2">
                            <label>Responsável (gerente / desenvolvedor)</label>
                            <select value={novaResponsavelId} onChange={(e) => setNovaResponsavelId(e.target.value)} className="form-control form-control-sm">
                                <option value="">Selecione...</option>
                                {analistas.map((a) => (
                                    <option key={a.USUSEQ ?? a.ususeq} value={a.USUSEQ ?? a.ususeq}>{getAnalistaDisplayNome(a.USRNOMCPL ?? a.usrnomcpl ?? a.USUSEQ ?? a.ususeq, a)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group mb-2">
                            <label>Data início</label>
                            <input type="date" value={novaDataInicio} onChange={(e) => setNovaDataInicio(e.target.value)} className="form-control form-control-sm" />
                        </div>
                        <div className="form-group mb-2">
                            <label>Data fim</label>
                            <input type="date" value={novaDataFim} onChange={(e) => setNovaDataFim(e.target.value)} className="form-control form-control-sm" />
                        </div>
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModalNova(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={handleCriarSprint}>Criar</button>
                        </div>
                    </div>
                </div>
            )}

            {showModalAdicionar && (
                <div className="modal-overlay" onClick={() => !loadingAdicionar && setShowModalAdicionar(false)}>
                    <div className="modal-card modal-card-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
                        <h5 className="mb-2">Adicionar chamados à sprint</h5>
                        <p className="text-muted small mb-3">Chamados abertos de tipos que <strong>usam sprint</strong>. Selecione os que deseja adicionar. Defina <strong>prioridade</strong> e <strong>Depende de</strong> (itens já na sprint que devem ser concluídos antes; use Ctrl+clique para múltiplos). Estimativa de horas é opcional.</p>
                        <div className="form-group mb-2">
                            <label className="small">Filtrar lista (número ou descrição)</label>
                            <input type="text" value={filtroListaAdicionar} onChange={(e) => setFiltroListaAdicionar(e.target.value)} className="form-control form-control-sm" placeholder="Digite para filtrar..." />
                        </div>
                        <div className="mb-3" style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
                            {chamadosAbertos.length === 0 && <p className="text-muted mb-0 p-3">Nenhum chamado elegível ou todos já estão na sprint.</p>}
                            {chamadosAbertos.length > 0 && chamadosFiltradosAdicionar().length === 0 && <p className="text-muted mb-0 p-3">Nenhum chamado corresponde ao filtro.</p>}
                            {chamadosFiltradosAdicionar().length > 0 && (
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th style={{ width: 36 }}></th>
                                            <th style={{ width: 70 }}>Número</th>
                                            <th>Descrição</th>
                                            <th style={{ width: 90 }}>Data</th>
                                            <th style={{ width: 70 }}>Prioridade (chamado)</th>
                                            <th style={{ width: 110 }}>Prioridade na sprint</th>
                                            <th style={{ width: 140 }} title="Itens já na sprint dos quais este depende">Depende de</th>
                                            <th style={{ width: 95 }} title="Somente chamados usa_sprint">Est. (h)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chamadosFiltradosAdicionar().map((c) => {
                                            const desc = c.descricao || c.SATDESSOL || '';
                                            const dependeDeVal = dependenciasPorChamado[c.id] ?? [];
                                            return (
                                                <tr key={c.id}>
                                                    <td className="align-middle">
                                                        <input type="checkbox" className="form-check-input" id={`add-${c.id}`} checked={selecionadosAdicionar.includes(c.id)} onChange={() => toggleSelecaoAdicionar(c.id)} />
                                                    </td>
                                                    <td className="align-middle">#{c.id}</td>
                                                    <td className="align-middle text-break" style={{ maxWidth: 320 }}>
                                                        <span className="cell-text-ellipsis d-inline-block" style={{ maxWidth: '100%' }} title={desc || undefined}>
                                                            {desc || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="align-middle small text-muted">{formatarDataLista(c.data || c.SATDATSOL)}</td>
                                                    <td className="align-middle">{c.prioridade != null ? c.prioridade : (c.SATPRINUM != null ? c.SATPRINUM : '—')}</td>
                                                    <td className="align-middle">
                                                        <select className={`form-select form-select-sm sprint-modal-prioridade sprint-modal-prioridade-${prioridadePorChamado[c.id] ?? 'media'}`} style={{ width: 110 }} value={prioridadePorChamado[c.id] ?? 'media'} onChange={(e) => setPrioridadeChamado(c.id, e.target.value)} title="Prioridade na sprint: Alta (vermelho), Média (amarelo), Baixa (verde)">
                                                            <option value="alta">Alta (vermelho)</option>
                                                            <option value="media">Média (amarelo)</option>
                                                            <option value="baixa">Baixa (verde)</option>
                                                        </select>
                                                    </td>
                                                    <td className="align-middle">
                                                        <select
                                                            multiple
                                                            className="form-select form-select-sm"
                                                            style={{ width: 130, minHeight: 56 }}
                                                            value={dependeDeVal.map(String)}
                                                            onChange={(e) => setDependenciasChamado(c.id, Array.from(e.target.selectedOptions, (o) => Number(o.value)))}
                                                            title="Itens já na sprint que devem ser concluídos antes (Ctrl+clique para múltiplos)"
                                                        >
                                                            {chamados.map((sc) => (
                                                                <option key={sc.sprint_chamado_id} value={sc.sprint_chamado_id}>#{sc.sat_num}</option>
                                                            ))}
                                                            {chamados.length === 0 && <option disabled>Nenhum na sprint</option>}
                                                        </select>
                                                    </td>
                                                    <td className="align-middle">
                                                        <input type="number" min="0" step="0.5" className="form-control form-control-sm" style={{ width: 72 }} placeholder="—" value={estimativasPorChamado[c.id] ?? ''} onChange={(e) => setEstimativaChamado(c.id, e.target.value)} title="Estimativa em horas (opcional)" />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModalAdicionar(false)} disabled={loadingAdicionar}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={confirmarAdicionar} disabled={loadingAdicionar || selecionadosAdicionar.length === 0}>{loadingAdicionar ? 'Adicionando...' : `Adicionar (${selecionadosAdicionar.length} selecionado${selecionadosAdicionar.length !== 1 ? 's' : ''})`}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Sprint;
